import React, { useState } from 'react';
import { X, Clock, Filter, Plus, CheckCircle } from 'lucide-react';
import Button from '../../../ui/Button';
import Select from '../../../ui/Select';
import ConditionsCard from '../common/ConditionsCard';
import ConditionDisplay from '../../../segments/ConditionDisplay';
import { Condition } from '../../../../types/segments/segments.types';

interface DelayPanelProps {
  onClose: () => void;
  onSave: (delayData: any) => void;
  conditions?: { [key: string]: Condition[] };
  onConditionsChange?: (conditions: { [key: string]: Condition[] }) => void;
  conditionsModified?: boolean;
  initialDelayValue?: number;
  initialDelayUnit?: string;
}

const DelayPanel: React.FC<DelayPanelProps> = ({
  onClose,
  onSave,
  conditions = {},
  onConditionsChange,
  conditionsModified = false,
  initialDelayValue = 0,
  initialDelayUnit = 'days',
}) => {
  const [delayValue, setDelayValue] = useState(initialDelayValue);
  const [delayUnit, setDelayUnit] = useState(initialDelayUnit);
  const [showConditionsCard, setShowConditionsCard] = useState(false);
  const [localConditions, setLocalConditions] = useState<{
    [key: string]: Condition[];
  }>(conditions);
  const [delaySettingsModified, setDelaySettingsModified] = useState(false);

  const hasConditions =
    Object.keys(conditions).length > 0 &&
    (conditions.and?.length > 0 || conditions.or?.length > 0);

  const handleSave = () => {
    onSave({
      delay: {
        value: delayValue,
        unit: delayUnit,
      },
      conditions: localConditions,
    });
    setDelaySettingsModified(false);
  };

  const handleConditionsSave = (conditionsData: any) => {
    setLocalConditions(conditionsData.conditions);
    if (onConditionsChange) {
      onConditionsChange(conditionsData.conditions);
    }
    setShowConditionsCard(false);
  };

  const handleConditionsChange = (newConditions: {
    [key: string]: Condition[];
  }) => {
    setLocalConditions(newConditions);
  };

  return (
    <div className="w-full flex">
      {showConditionsCard && (
        <ConditionsCard
          onClose={() => setShowConditionsCard(false)}
          onSave={handleConditionsSave}
          initialConditions={localConditions}
          onConditionsChange={handleConditionsChange}
        />
      )}
      <div className="flex flex-col bg-card rounded-lg shadow-sm border border-border w-[30vw] h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Clock className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                Time Delay
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Wait before the next step
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
            {/* Delay Section */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-medium text-foreground">Delay</h3>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  Wait
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={delayValue}
                    onChange={(e) => {
                      setDelayValue(Number(e.target.value));
                      setDelaySettingsModified(true);
                    }}
                    className="w-20 px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    min={0}
                  />
                  <Select
                    value={delayUnit}
                    onChange={(e) => {
                      setDelayUnit(e.target.value);
                      setDelaySettingsModified(true);
                    }}
                    options={[
                      { value: 'minutes', label: 'minutes' },
                      { value: 'hours', label: 'hours' },
                      { value: 'days', label: 'days' },
                      { value: 'weeks', label: 'weeks' },
                      { value: 'months', label: 'months' },
                    ]}
                    className="flex-1 min-w-0"
                  />
                </div>
              </div>
            </div>

            {/* Conditions Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Filter className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground">
                      Conditions
                    </h3>
                    {hasConditions && (
                      <p className="text-xs mt-0.5 text-primary font-medium flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Configured
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowConditionsCard(true)}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <p className="text-sm text-muted-foreground">
                Check whether people meet specific conditions before they enter
                the delay or perform the action.
              </p>

              {hasConditions && (
                <div className="p-3 rounded-lg border border-border bg-muted/30">
                  <ConditionDisplay conditions={conditions} />
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => setShowConditionsCard(true)}
                className="w-full text-foreground border-border hover:bg-muted"
              >
                {hasConditions ? 'Edit conditions' : 'Add condition'}
              </Button>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border shrink-0 space-y-3">
            <Button
              variant="primary"
              disabled={!conditionsModified && !delaySettingsModified}
              onClick={handleSave}
              className="w-full"
            >
              Save and build workflow
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full border-border text-foreground hover:bg-muted"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DelayPanel;
